import React, { useEffect, useRef, useMemo } from 'react';

const ParallaxBackground = ({ image, children, className = '', overlay = true }) => {
  const containerRef = useRef(null);
  const backgroundRef = useRef(null);
  const rafRef = useRef(null);

  // Memoize the background style to prevent unnecessary re-renders
  const backgroundStyle = useMemo(() => ({
    backgroundImage: `url(${image})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    willChange: 'transform',
    transform: 'translate3d(0, 0, 0)',
  }), [image]);

  useEffect(() => {
    let ticking = false;

    const updateParallax = () => {
      if (!containerRef.current || !backgroundRef.current) return;

      const container = containerRef.current;
      const background = backgroundRef.current;

      const rect = container.getBoundingClientRect();
      const windowHeight = window.innerHeight;

      // Calculate if container is in view
      if (rect.top < windowHeight && rect.bottom > 0) {
        const viewportCenter = windowHeight / 2;
        const containerCenter = rect.top + rect.height / 2;
        const distanceFromCenter = containerCenter - viewportCenter;

        // Use translate3d for hardware acceleration and smoother performance
        // Reduced multiplier from 0.15 to 0.08 for subtler effect and no edge exposure
        const offset = distanceFromCenter * 0.08;
        background.style.transform = `translate3d(0, ${offset}px, 0)`;
      }

      ticking = false;
    };

    const onScroll = () => {
      if (!ticking) {
        rafRef.current = requestAnimationFrame(updateParallax);
        ticking = true;
      }
    };

    // Use passive listener for better scroll performance
    window.addEventListener('scroll', onScroll, { passive: true });
    // Initial calculation
    updateParallax();

    return () => {
      window.removeEventListener('scroll', onScroll);
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, []);

  return (
    <div ref={containerRef} className={`relative overflow-hidden ${className}`}>
      {/* Parallax Background Image - Extended to prevent white edges */}
      <div
        ref={backgroundRef}
        className="absolute w-full"
        style={{
          ...backgroundStyle,
          top: '-10%',
          bottom: '-10%',
          height: '120%',
        }}
        aria-hidden="true"
      />

      {/* Dark Overlay */}
      {overlay && (
        <div className="absolute inset-0 bg-gradient-to-br from-eagle-dark/85 via-eagle-dark/80 to-eagle-blue/85" aria-hidden="true" />
      )}

      {/* Content */}
      <div className="relative z-10">
        {children}
      </div>
    </div>
  );
};

export default ParallaxBackground;
